// Message builders — the single place every writer calls to turn semantic
// inputs (kind, content, payload, mentions) into a fully-derived message row
// plus side-table rows. Writers never hand-roll preview_text or the
// calendar_* / email_* / terminal_* / call_duration_seconds columns.
//
// This module is the public entry. The pipeline internals live in topical
// siblings: payload validation (`message-payload-validation`), structured-
// column + side-table derivation (`message-column-derivation`), coercion
// primitives (`message-coerce`), and the shared shapes (`message-builder-types`).
//
// Authority split for side tables:
//   - calendar_attendees, email_participants, call_participants:
//     written by builders here, on both the client and server transaction
//     branches of the owning mutator.
//   - message_mentions: builders derive junction rows from `args.mentions`
//     and emit them via sideTableRows.message_mentions; applyBuiltMessage
//     (Zero tx) and applyBuiltMessageSql (raw SQL) insert with upsert /
//     ON CONFLICT DO NOTHING semantics. The legacy
//     `sync_message_mentions_from_jsonb` trigger was dropped in migration
//     117 (Phase 7c-C) — application code is now the sole writer. The
//     UPDATE-of-mentions path (message edits) is handled by
//     `syncMessageMentionsJunction` below; the JSONB column stays
//     populated until 7c-D's column drop.

import { MessageKind, SERVER_ONLY_KINDS } from "./message-payload";
import { renderPreview } from "./message-preview";
import { validatePayload } from "./message-payload-validation";
import {
  extractSideTableRows,
  extractStructuredColumns,
} from "./message-column-derivation";
import type {
  BuildInsertArgs,
  BuiltMessage,
  EditPatch,
  EditPatchInput,
  MutateSurface,
} from "./message-builder-types";

export type {
  BuildInsertArgs,
  BuiltMessage,
  CalendarAttendeeRow,
  CallParticipantRow,
  EditPatch,
  EditPatchInput,
  EmailParticipantRow,
  MessageMentionRow,
  MutateSurface,
} from "./message-builder-types";

/**
 * Derive the full message row + side-table rows from semantic inputs.
 *
 * Pure — no I/O, safe to call on both client and server branches of a
 * mutator. Does NOT enforce SERVER_ONLY_KINDS (that gate lives in the
 * mutator body since it requires `tx.location`).
 */
export function buildMessage(args: BuildInsertArgs): BuiltMessage {
  const payload = args.payload ?? null;
  const validatedPayload = validatePayload(args.kind, payload);
  const structured = extractStructuredColumns(args.kind, validatedPayload);
  const preview_text = renderPreview({
    kind: args.kind,
    content: args.content,
    calendar_title: structured.calendar_title,
    calendar_start_at: structured.calendar_start_at,
    email_subject: structured.email_subject,
    email_from_email: structured.email_from_email,
    terminal_command: structured.terminal_command,
    terminal_exit_code: structured.terminal_exit_code,
    call_duration_seconds: structured.call_duration_seconds,
    payload: validatedPayload,
  });

  const sideTableRows = extractSideTableRows(
    args.id,
    args.kind,
    validatedPayload,
    args.mentions ?? null,
  );

  // Phase 7c-D: `mentions` JSONB column dropped (mig 127). The
  // `args.mentions` array is still consumed by the builder — it
  // populates `sideTableRows.message_mentions` (the junction) above.
  // No field on `built.message` needed; downstream `applyBuiltMessage`
  // / `applyBuiltMessageSql` insert junction rows from sideTableRows.
  return {
    message: {
      id: args.id,
      channel_id: args.channel_id,
      user_id: args.user_id,
      content: args.content,
      kind: args.kind,
      payload: validatedPayload,
      preview_text,
      thread_id: args.thread_id ?? null,
      reply_count: args.reply_count ?? 0,
      reply_users: args.reply_users ?? null,
      latest_reply_at: args.latest_reply_at ?? null,
      replied_to_message_id: args.replied_to_message_id ?? null,
      is_broadcast: args.is_broadcast ?? false,
      is_edited: args.is_edited ?? false,
      sender_display_name: args.sender_display_name ?? null,
      sender_avatar_url: args.sender_avatar_url ?? null,
      created_at: args.created_at,
      updated_at: args.updated_at,
      ...structured,
    },
    sideTableRows,
  };
}

/**
 * Apply a built message inside a mutator transaction — insert the message
 * row, then each side-table row. Callers are responsible for running auth
 * checks BEFORE invoking this helper.
 */
export async function applyBuiltMessage(
  tx: MutateSurface,
  built: BuiltMessage,
): Promise<void> {
  await tx.mutate.messages.insert(built.message);
  for (const row of built.sideTableRows.calendar_attendees) {
    await tx.mutate.calendar_attendees.insert(row);
  }
  for (const row of built.sideTableRows.email_participants) {
    await tx.mutate.email_participants.insert(row);
  }
  for (const row of built.sideTableRows.call_participants) {
    await tx.mutate.call_participants.insert(row);
  }
  // Phase 7c-C: builders are now the sole writer of `message_mentions`
  // junction rows on INSERT (`sync_message_mentions_from_jsonb` trigger
  // dropped in migration 117). `.upsert()` keeps the call idempotent
  // for any caller that re-applies a built message during reconciliation.
  // The UPDATE-of-mentions path lives in `syncMessageMentionsJunction`
  // below — called by `mutators.messages.update` since builders run
  // INSERT-only.
  for (const row of built.sideTableRows.message_mentions) {
    await tx.mutate.message_mentions.upsert(row);
  }
}

/**
 * Phase 7c-C: replacement for the dropped `sync_message_mentions_from_jsonb`
 * trigger's UPDATE-of-mentions path. Caller passes the new mentions array
 * (or null/undefined to clear); we DELETE existing junction rows and
 * INSERT the new set. Empty strings filtered, duplicates deduped — same
 * semantics the trigger encoded.
 *
 * Server-only. The client branch can't reliably enumerate existing rows
 * (`tx.run()` returns cached data) and would risk leaving stale rows.
 * Skipping client-side optimistic junction sync is safe because no
 * client-rendered surface depends on the junction today — desktop + iOS
 * still render mentions from `messages.mentions` JSONB until 7c-D flips
 * those readers and drops the column.
 */
export async function syncMessageMentionsJunction(
  tx: MutateSurface,
  zql: { message_mentions: { where: (col: string, val: string) => unknown } },
  messageId: string,
  mentions: string[] | null | undefined,
): Promise<void> {
  if (tx.location !== "server") return;

  const existing = await tx.run(
    zql.message_mentions.where("message_id", messageId),
  );
  for (const row of existing as Array<{
    message_id: string;
    user_id: string;
  }>) {
    await tx.mutate.message_mentions.delete({
      message_id: row.message_id,
      user_id: row.user_id,
    });
  }

  if (!mentions || mentions.length === 0) return;
  const seen = new Set<string>();
  for (const raw of mentions) {
    if (typeof raw !== "string") continue;
    if (raw === "") continue;
    if (seen.has(raw)) continue;
    seen.add(raw);
    await tx.mutate.message_mentions.upsert({
      message_id: messageId,
      user_id: raw,
    });
  }
}

/**
 * Throw if a client attempts to stamp a server-only kind. Callers supply
 * `tx.location` so the gate is enforced on the server branch even for
 * mutators that accept client inserts for other kinds.
 */
export function assertClientMaySetKind(
  kind: MessageKind,
  location: "client" | "server",
): void {
  if (location === "server") return;
  if (SERVER_ONLY_KINDS.has(kind)) {
    throw new Error(
      `Unauthorized: kind "${kind}" is server-only and cannot be stamped by a client.`,
    );
  }
}

/**
 * Build a patch for the `update` mutator. `kind` is immutable after insert
 * (the mutator also strips it); content and/or payload edits re-derive
 * preview_text + structured columns. Side tables are re-derived by the
 * caller via DELETE-then-INSERT if payload changes in a way that affects
 * them.
 */
export function buildEditPatch(input: EditPatchInput): EditPatch {
  const nextContent = input.changes.content ?? input.existing.content;
  const nextPayload =
    "payload" in input.changes
      ? (input.changes.payload ?? null)
      : (input.existing.payload ?? null);
  const validatedPayload = validatePayload(input.existing.kind, nextPayload);
  const structured = extractStructuredColumns(
    input.existing.kind,
    validatedPayload,
  );
  const preview_text = renderPreview({
    kind: input.existing.kind,
    content: nextContent,
    calendar_title: structured.calendar_title,
    calendar_start_at: structured.calendar_start_at,
    email_subject: structured.email_subject,
    email_from_email: structured.email_from_email,
    terminal_command: structured.terminal_command,
    terminal_exit_code: structured.terminal_exit_code,
    call_duration_seconds: structured.call_duration_seconds,
    payload: validatedPayload,
  });

  const patch: EditPatch = {
    id: input.id,
    preview_text,
    ...structured,
    is_edited: true,
    updated_at: input.updated_at,
  };
  if (input.changes.content !== undefined) patch.content = nextContent;
  if ("payload" in input.changes) patch.payload = validatedPayload;
  return patch;
}
